import React, { useState, useEffect, useMemo } from 'react';
import { 
  FaArrowLeft, FaUserGraduate, FaBuilding, FaLayerGroup, 
  FaCheckSquare, FaSquare, FaLevelUpAlt, FaSearch
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api, { BASE_URL } from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import useColleges from '../../../utils/useColleges';
import './PromoteStudents.css';

const API_BASE = BASE_URL;

const PromoteStudents = () => {
  const { user } = useAuth();
  const isAdmin  = user?.role === 'admin' || (user?.colleges?.length > 0);
  const { colleges, loading: collegesLoading } = useColleges();
  const navigate = useNavigate();

  const [selectedCollege, setSelectedCollege] = useState('');
  const [fromSession, setFromSession]         = useState('');
  const [toSession, setToSession]             = useState('');
  const [selectedClass, setSelectedClass]     = useState('');
  
  const [sessions, setSessions]   = useState([]);
  const [classes, setClasses]     = useState([]);
  const [students, setStudents]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [promoting, setPromoting] = useState(false);

  const [selectedIds, setSelectedIds] = useState([]);

  // Auto-set College
  useEffect(() => {
    if (!isAdmin && colleges.length > 0) setSelectedCollege(String(colleges[0]._id));
    else if (isAdmin && colleges.length === 1) setSelectedCollege(String(colleges[0]._id));
  }, [isAdmin, colleges]);

  // Fetch Sessions and Classes
  useEffect(() => {
    if (!selectedCollege) {
      setSessions([]); setClasses([]); setFromSession(''); setToSession(''); setSelectedClass('');
      return;
    }

    api.get('/sessions', { params: { college: selectedCollege } })
      .then(res => {
        const data = res.data.data || [];
        setSessions(data);
        const active = data.find(s => s.isActive);
        if (active) setFromSession(active._id);
        else if (data.length > 0) setFromSession(data[0]._id);
        setToSession('');
      }).catch(() => setSessions([]));

    api.get('/classes', { params: { college: selectedCollege } })
      .then(res => setClasses(res.data.data || []))
      .catch(() => setClasses([]));
  }, [selectedCollege]);

  // Fetch Students
  useEffect(() => {
    if (!selectedCollege || !fromSession) {
      setStudents([]);
      setSelectedIds([]);
      return;
    }
    
    setLoading(true);
    const params = { college: selectedCollege, session: fromSession, limit: 1000 };
    if (selectedClass) params.class = selectedClass;

    api.get('/students', { params })
      .then(res => {
        const data = res.data.data || [];
        // Filter out students who are already passed out or inactive if needed
        const activeStudents = data.filter(s => s.isActive && s.currentYear !== 'Passout');
        setStudents(activeStudents);
        // Select all by default
        setSelectedIds(activeStudents.map(s => s._id));
      })
      .catch(() => {
        setStudents([]);
        setSelectedIds([]);
      })
      .finally(() => setLoading(false));
  }, [selectedCollege, fromSession, selectedClass]);

  // Get start year from session name e.g. "2024-25" -> 2024
  const getSessionYear = (sessionId) => {
    const s = sessions.find(x => x._id === sessionId);
    return s?.name ? parseInt(s.name.split('-')[0]) : 0;
  };

  // Only allow exactly 1 year ahead — block 2+ year jumps
  const toSessionOptions = useMemo(() => {
    const fromYear = getSessionYear(fromSession);
    return sessions.filter(s => s._id !== fromSession && getSessionYear(s._id) === fromYear + 1);
  }, [sessions, fromSession]);

  const fromSessionName = sessions.find(s => s._id === fromSession)?.name || '';
  const toSessionName   = sessions.find(s => s._id === toSession)?.name   || '';

  // Use student.currentYear from DB, map to next year
  const getStudentStatus = (student) => {
    const YEAR_MAP = {
      '1st Year': '2nd Year',
      '2nd Year': '3rd Year',
      '3rd Year': '4th Year',
      '4th Year': '5th Year',
      '5th Year': 'Passout',
    };
    const duration    = parseInt(student.class?.duration) || 99;
    const currentYear = student.currentYear || '1st Year';
    const currentNum  = parseInt(currentYear.replace(/[^0-9]/g, '')) || 1;
    const isPassout   = currentNum >= duration;
    const next        = isPassout ? 'Passout' : (YEAR_MAP[currentYear] || 'Passout');
    return { current: currentYear, next, isPassout };
  };

  // Toggle Selection
  const toggleSelectAll = () => {
    if (selectedIds.length === students.length) setSelectedIds([]);
    else setSelectedIds(students.map(s => s._id));
  };

  const toggleStudent = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(sId => sId !== id) : [...prev, id]);
  };

  // Handle Promote
  const handlePromote = async () => {
    if (selectedIds.length === 0) {
      return Swal.fire('No Selection', 'Please select at least one student to promote.', 'warning');
    }
    if (!toSession) {
      return Swal.fire('Missing Session', 'Please select the Target Session to promote these students into.', 'warning');
    }
    if (fromSession === toSession) {
      return Swal.fire('Invalid Session', 'Target session cannot be the same as the current session.', 'warning');
    }

    const result = await Swal.fire({
      title: 'Confirm Promotion',
      html: `
        <div style="text-align:left; font-size:0.95rem; line-height:1.8">
          <div style="background:#f0fdf4; border:1px solid #bbf7d0; border-radius:8px; padding:12px 16px; margin-bottom:12px; text-align:center">
            <span style="font-size:1.1rem; font-weight:700; color:#15803d">${fromSessionName}</span>
            <span style="margin:0 10px; color:#64748b; font-size:1.2rem">→</span>
            <span style="font-size:1.1rem; font-weight:700; color:#1d4ed8">${toSessionName}</span>
          </div>
          <div>Students selected: <b>${selectedIds.length}</b></div>
          <div style="margin-top:6px; font-size:0.85rem; color:#64748b">Students at max duration will be marked as <b>Passout</b>.</div>
        </div>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, Promote'
    });

    if (result.isConfirmed) {
      setPromoting(true);
      try {
        await Promise.all(selectedIds.map(studentId => {
          const student = students.find(s => s._id === studentId);
          const status  = getStudentStatus(student);
          const newYear = status.isPassout ? 'Passout' : status.next;
          // amazonq-ignore-next-line
          return api.post(`/students/${studentId}/promote`, {
            newSessionId: toSession,
            newYear,
          });
        }));

        Swal.fire('Success!', `${selectedIds.length} students have been promoted successfully.`, 'success');
        
        // Remove promoted students from the current view
        setStudents(prev => prev.filter(s => !selectedIds.includes(s._id)));
        setSelectedIds([]);
        
      } catch (err) {
        Swal.fire('Error', err.response?.data?.message || 'Failed to promote students.', 'error');
      } finally {
        setPromoting(false);
      }
    }
  };

  return (
    <div className="promote-students-container">

      {/* ── Header ── */}
      <div className="promote-students-header">
        <button className="promote-students-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
        <h2 className="promote-students-title">Promote Students</h2>
      </div>

      {/* ── Filters ── */}
      <div className="promote-students-filters-wrapper">
        
        {isAdmin && (
          <div className="promote-students-filter-group">
            <label>College</label>
            <select value={selectedCollege} onChange={e => setSelectedCollege(e.target.value)}>
              <option value="">-- Select College --</option>
              {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
        )}

        <div className="promote-students-filter-group">
          <label>From Batch (Current Session)</label>
          <select value={fromSession} onChange={e => setFromSession(e.target.value)} disabled={!selectedCollege}>
            <option value="">-- Select Session --</option>
            {sessions.map(s => <option key={s._id} value={s._id}>{s.name} {s.isActive ? '(Active)' : ''}</option>)}
          </select>
        </div>

        <div className="promote-students-filter-group">
          <label>Department / Class</label>
          <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)} disabled={!selectedCollege}>
            <option value="">All Classes & Departments</option>
            {classes.map(c => <option key={c._id} value={c._id}>{c.name} {c.medium ? `(${c.medium})` : ''}</option>)}
          </select>
        </div>

        <div className="promote-students-filter-group highlight-filter">
          <label>Target Session (Promote To)</label>
          <select value={toSession} onChange={e => setToSession(e.target.value)} disabled={!fromSession}>
            <option value="">-- Select Target Session --</option>
            {toSessionOptions.map(s => (
              <option key={s._id} value={s._id}>{s.name}</option>
            ))}
          </select>
          {fromSession && toSessionOptions.length === 0 && (
            <span style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: 4, display: 'block' }}>No next session found. Please create session for next year first.</span>
          )}
        </div>

      </div>

      {/* ── Action Info Bar ── */}
      {selectedCollege && fromSession && (
        <div className="promote-students-info-bar">
          <span className="promote-students-count">
            Showing <b>{students.length}</b> eligible student(s)
          </span>
          <span className="promote-students-selected-count">
            {selectedIds.length} Selected
          </span>
        </div>
      )}

      {/* ── Table ── */}
      <div className="promote-students-table-wrapper">
        <table className="promote-students-table">
          <thead>
            <tr>
              <th className="promote-students-col-checkbox">
                <div className="promote-students-checkbox-wrapper" onClick={toggleSelectAll}>
                  {selectedIds.length === students.length && students.length > 0 
                    ? <FaCheckSquare className="promote-students-chk active" /> 
                    : <FaSquare className="promote-students-chk" />}
                </div>
              </th>
              <th>S.No</th>
              <th>Photo</th>
              <th className="promote-students-align-left">Student Name</th>
              <th>Class</th>
              <th>Current Year</th>
              <th>Next Status (After Promotion)</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="promote-students-empty">Loading students...</td></tr>
            ) : !selectedCollege || !fromSession ? (
              <tr><td colSpan="7" className="promote-students-empty">Please select a college and session.</td></tr>
            ) : students.length === 0 ? (
              <tr><td colSpan="7" className="promote-students-empty">No eligible students found in this batch.</td></tr>
            ) : (
              students.map((student, i) => {
                const photoUrl = student.photo ? `${API_BASE}/${student.photo}` : null;
                const status = getStudentStatus(student);
                const isSelected = selectedIds.includes(student._id);

                return (
                  <tr key={student._id} className={`promote-students-row ${isSelected ? 'selected-row' : ''}`}>
                    <td className="promote-students-col-checkbox">
                      <div className="promote-students-checkbox-wrapper" onClick={() => toggleStudent(student._id)}>
                        {isSelected ? <FaCheckSquare className="promote-students-chk active" /> : <FaSquare className="promote-students-chk" />}
                      </div>
                    </td>
                    <td>{i + 1}</td>
                    <td>
                      <div className="promote-students-photo-wrap">
                        {photoUrl ? (
                          <img src={photoUrl} alt={student.name} className="promote-students-photo" 
                            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                        ) : null}
                        <div className="promote-students-photo-fallback" style={{ display: photoUrl ? 'none' : 'flex' }}>
                          <FaUserGraduate />
                        </div>
                      </div>
                    </td>
                    <td className="promote-students-align-left promote-students-name-text">
                      {student.name}
                      <span className="promote-students-adm-subtext">{student.admissionNo || 'No ID'}</span>
                    </td>
                    <td><span className="promote-students-badge-class">{student.class?.name || student.className || '—'}</span></td>
                    <td><span className="promote-students-badge-year current">{status.current}</span></td>
                    <td>
                      <span className={`promote-students-badge-year next ${status.isPassout ? 'passout' : 'promoted'}`}>
                        {status.next}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Floating Action Bar ── */}
      {students.length > 0 && (
        <div className="promote-students-action-bar">
          <div className="promote-students-action-info">
            You have selected <strong>{selectedIds.length}</strong> students for promotion.
          </div>
          <button 
            className="promote-students-btn-submit" 
            onClick={handlePromote}
            disabled={promoting || selectedIds.length === 0}
          >
            {promoting ? 'Promoting...' : <><FaLevelUpAlt /> Promote Selected Students</>}
          </button>
        </div>
      )}

    </div>
  );
};

export default PromoteStudents;